import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useBackDismiss } from 'utils/hooks/useBackDismiss';

interface ChatEntry {
  /** URL for the chat's own history entry; the list's entry keeps the plain one. */
  path?: string;
  /** Run when the back button leaves the chat. */
  onLeave?: () => void;
}

interface MobileNavigationContextProps {
  /** 0 = chat list, 1 = chat, 2 = contact details. */
  showSection: number;
  /** Pass `chat` when opening one: it then owns a history entry until section 0. */
  setShowSection: (section: number, chat?: ChatEntry) => void;
  /** For the chat's back arrow. False when there is nothing to step back to. */
  goBack: () => boolean;
}

const MobileNavigationContext = createContext<
  MobileNavigationContextProps | undefined
>(undefined);

// On desktop too, as before: Back closes contact details, then the chat.
const EVERY_DEVICE = { always: true };

export const MobileNavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [showSection, setSection] = useState<number>(0);
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const chat = useRef<ChatEntry>({});

  const setShowSection = useCallback((section: number, entry?: ChatEntry) => {
    if (section === 0) {
      setChatOpen(false);
    } else if (entry) {
      chat.current = entry;
      setChatOpen(true);
    }
    setSection(section);
  }, []);

  const goBack = useBackDismiss(
    chatOpen,
    () => {
      chat.current.onLeave?.();
      setChatOpen(false);
      setSection(0);
    },
    { ...EVERY_DEVICE, path: chat.current.path },
  );
  useBackDismiss(showSection === 2, () => setSection(1), EVERY_DEVICE);

  const value = useMemo(
    () => ({ showSection, setShowSection, goBack }),
    [showSection, setShowSection, goBack],
  );

  return (
    <MobileNavigationContext.Provider value={value}>
      {children}
    </MobileNavigationContext.Provider>
  );
};

export const useMobileNavigation = (): MobileNavigationContextProps => {
  const context = useContext(MobileNavigationContext);
  if (!context) {
    throw new Error(
      'useMobileNavigation must be used within a MobileNavigationProvider',
    );
  }
  return context;
};
